import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { updateEmpresaConfianza, EmpresaConfianza } from '../../services/empresaConfianzaService';
import ImageUpload from '../Upload/ImageUpload';

interface EditEmpresasConfianzaPageProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresa: EmpresaConfianza | null;
}

export const EditEmpresasConfianzaPage: React.FC<EditEmpresasConfianzaPageProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresa,
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    rubro: '',
    descripcion: '',
    logo_alt: '',
    orden: 0,
    activo: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (empresa) {
      setFormData({
        nombre: empresa.nombre,
        rubro: empresa.rubro,
        descripcion: empresa.descripcion,
        logo_alt: empresa.logo_alt || '',
        orden: empresa.orden,
        activo: empresa.activo,
      });
      setLogoFile(null);
      setError('');
    }
  }, [empresa, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresa) return;
    setError('');
    setLoading(true);

    try {
      await updateEmpresaConfianza(empresa.id, {
        nombre: formData.nombre,
        rubro: formData.rubro,
        descripcion: formData.descripcion,
        logo: logoFile,
        logo_alt: formData.logo_alt,
        orden: formData.orden,
        activo: formData.activo,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError('Error al actualizar la empresa. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !empresa) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-gray-900">Editar Empresa de Confianza</h3>
          <div className="flex items-center gap-4">
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
              <input
                type="text"
                name="nombre"
                required
                value={formData.nombre}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rubro *</label>
              <input
                type="text"
                name="rubro"
                required
                value={formData.rubro}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción *</label>
              <textarea
                name="descripcion"
                required
                rows={3}
                value={formData.descripcion}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
              <ImageUpload
                setImagen={setLogoFile}
                currentImage={empresa.logo ?? undefined} // ✅ Usamos 'logo' en lugar de 'logo_url'
              />
              <p className="text-xs text-gray-500 mt-1">Dejar vacío para mantener el logo actual</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Texto alternativo del logo</label>
              <input
                type="text"
                name="logo_alt"
                value={formData.logo_alt}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Orden</label>
              <input
                type="number"
                name="orden"
                value={formData.orden}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
              <div>
                <span className="text-sm font-medium text-gray-700">Activo</span>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.activo ? 'La empresa estará visible' : 'La empresa no se mostrará'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="activo"
                  className="sr-only"
                  checked={formData.activo}
                  onChange={handleChange}
                />
                <div className={`w-10 h-6 rounded-full transition-colors ${formData.activo ? 'bg-[#c1348b]' : 'bg-gray-300'}`}>
                  <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${formData.activo ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-sm bg-[#c1348b] text-white rounded-lg transition-colors font-medium flex items-center ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#8f1f64]'}`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Actualizando...
                </span>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmpresasConfianzaPage;